import React, { useState } from 'react';
import { X, UploadCloud, FileCode, CheckCircle2, AlertCircle, Sparkles, Copy } from 'lucide-react';
import { useLanguage } from '../lib/i18n';
import { apiPost } from '../lib/api';
import { ExerciseProgram } from '../types';

interface ProgramJsonImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess: (imported: ExerciseProgram[]) => void;
}

const SAMPLE_PROGRAMS: Record<'fa' | 'en' | 'sv', any> = {
  fa: [
    {
      "title": "برنامه ۲۱ روزه درمان دیسک کمر و تقویت فیله (پروتکل کلینیکی)",
      "description": "برنامه تخصصی ۳ مرحله‌ای ریکاوری ستون فقرات شامل کشش پسواس، دکومپرشن غیرفعال، تقویت ایزومتریک و پل عضلانی.",
      "category": "rehab",
      "difficulty": "Intermediate",
      "isActive": true,
      "days": [
        {
          "dayNumber": 1,
          "title": "روز ۱ — دکومپرشن و آزادسازی پسواس",
          "notes": "انجام تمرینات آرامش‌بخش ستون فقرات در ابتدای روز یا قبل از خواب",
          "exercises": [
            {
              "name": "کشش پسواس با متکا (Psoas Decompression)",
              "category": "rehab",
              "purpose": "کاهش قوس لوردوز و کاهش فشار دیسک‌های L4-L5",
              "targetMuscles": "عضله پسواس، مربع کمری",
              "sets": 3,
              "reps": "۶۰ ثانیه",
              "rest": "۳۰ ثانیه",
              "duration": 5,
              "difficulty": "Beginner",
              "description": "روی سطح صاف دراز بکشید، متکایی زیر زانوها قرار دهید و نفس عمیق شکمی بکشید."
            },
            {
              "name": "تمرین ژست گربه-گاو آرام (Cat-Cow)",
              "category": "rehab",
              "purpose": "افزایش جریان مایع بین‌مفصلی ستون فقرات",
              "targetMuscles": "کل ستون فقرات و فیله کمر",
              "sets": 3,
              "reps": "۱۰ تکرار",
              "rest": "۲۰ ثانیه",
              "duration": 4,
              "difficulty": "Beginner",
              "description": "روی چهار دست و پا قرار گرفته و با دم کمر را گود و با بازدم گرد کنید."
            }
          ]
        },
        {
          "dayNumber": 2,
          "title": "روز ۲ — تقویت ایزومتریک ثبات‌دهنده‌های عمقی",
          "notes": "تمرکز روی انقباض شکم بدون حرکت دادن ستون فقرات",
          "exercises": [
            {
              "name": "پل همسترینگ و باسن (Glute Bridge)",
              "category": "rehab",
              "purpose": "فعال‌سازی عضلات سرینی و حمایت از لگن",
              "targetMuscles": "عضلات باسن، فیله کمر",
              "sets": 3,
              "reps": "۱۲ تکرار",
              "rest": "۳۰ ثانیه",
              "duration": 5,
              "difficulty": "Intermediate",
              "description": "به پشت دراز بکشید، زانوها را خم کنید و لگن را تا امتداد زانو بالا بیاورید."
            },
            {
              "name": "تمرین سگ-پرنده استاتیک (Bird-Dog Hold)",
              "category": "rehab",
              "purpose": "تثبیت متقابل فیله کمر و سرینی",
              "targetMuscles": "عضلات مورب شکم، فیله کمر",
              "sets": 3,
              "reps": "۵ تکرار ۱۰ ثانیه‌ای هر طرف",
              "rest": "۳۰ ثانیه",
              "duration": 5,
              "difficulty": "Intermediate",
              "description": "دست راست و پای چپ را همزمان در امتداد بدن دراز کنید."
            }
          ]
        }
      ]
    }
  ],
  en: [
    {
      "title": "21-Day Lumbar Spine & Core Recovery Protocol",
      "description": "Clinical 3-stage spinal decompression and core stabilization protocol including psoas release, gentle mobility, and glute bridges.",
      "category": "rehab",
      "difficulty": "Intermediate",
      "isActive": true,
      "days": [
        {
          "dayNumber": 1,
          "title": "Day 1 — Decompression & Psoas Release",
          "notes": "Perform gentle spinal decompression exercises early in the morning or before sleep.",
          "exercises": [
            {
              "name": "Psoas Decompression with Bolster",
              "category": "rehab",
              "purpose": "Reduces mechanical load on L4-L5 discs and relaxes hip flexors.",
              "targetMuscles": "Psoas major, quadratus lumborum",
              "sets": 3,
              "reps": "60 seconds",
              "rest": "30 seconds",
              "duration": 5,
              "difficulty": "Beginner",
              "description": "Lie flat on the floor with lower legs resting on a bolster or chair; perform slow diaphragmatic breathing."
            },
            {
              "name": "Gentle Cat-Cow Mobility",
              "category": "rehab",
              "purpose": "Restores intervertebral joint mobility and fluid circulation.",
              "targetMuscles": "Entire spine, erector spinae",
              "sets": 3,
              "reps": "10 reps",
              "rest": "20 seconds",
              "duration": 4,
              "difficulty": "Beginner",
              "description": "Start on hands and knees; arch spine gently on inhale and round on exhale."
            }
          ]
        },
        {
          "dayNumber": 2,
          "title": "Day 2 — Isometric Deep Core & Pelvic Stabilization",
          "notes": "Focus on abdominal bracing without spinal movement.",
          "exercises": [
            {
              "name": "Glute Bridge Activation",
              "category": "rehab",
              "purpose": "Activates gluteal muscles and stabilizes the pelvis.",
              "targetMuscles": "Glutes, hamstrings, lower back",
              "sets": 3,
              "reps": "12 reps",
              "rest": "30 seconds",
              "duration": 5,
              "difficulty": "Intermediate",
              "description": "Lie on back with knees bent; press through heels to lift hips."
            },
            {
              "name": "McGill Bird-Dog Core Hold",
              "category": "rehab",
              "purpose": "Diagonal core and lumbar stabilization.",
              "targetMuscles": "Multifidus, obliques, erector spinae",
              "sets": 3,
              "reps": "5 reps per side (10s hold)",
              "rest": "30 seconds",
              "duration": 5,
              "difficulty": "Intermediate",
              "description": "Extend right arm and left leg simultaneously while keeping torso rigid."
            }
          ]
        }
      ]
    }
  ],
  sv: [
    {
      "title": "21-dagars rehabiliteringsprogram för ländrygg och bålstabilitet",
      "description": "Kliniskt 3-stegs protokoll för dekompression och stabilisering inklusive psoas-stretch, rörlighet och höftlyft.",
      "category": "rehab",
      "difficulty": "Intermediate",
      "isActive": true,
      "days": [
        {
          "dayNumber": 1,
          "title": "Dag 1 — Dekompression & Psoas-avlastning",
          "notes": "Utför skonsamma avlastningsövningar på morgonen eller före sänggående.",
          "exercises": [
            {
              "name": "Psoas-avlastning med kudde",
              "category": "rehab",
              "purpose": "Minskar mekanisk belastning på L4-L5 diskar och slappnar av höftböjare.",
              "targetMuscles": "Psoas major, quadratus lumborum",
              "sets": 3,
              "reps": "60 sekunder",
              "rest": "30 sekunder",
              "duration": 5,
              "difficulty": "Beginner",
              "description": "Ligg på rygg med underbenen vilande på en kudde eller stol; ta djupa bukandetag."
            },
            {
              "name": "Skonsam Katt-Ko Rörlighet",
              "category": "rehab",
              "purpose": "Återställer rörlighet i ryggraden och ökar cirkulation av ledvätska.",
              "targetMuscles": "Hela ryggraden, ryggsträckare",
              "sets": 3,
              "reps": "10 reps",
              "rest": "20 sekunder",
              "duration": 4,
              "difficulty": "Beginner",
              "description": "Stå på alla fyra; svanka försiktigt vid inandning och krumma vid utandning."
            }
          ]
        },
        {
          "dayNumber": 2,
          "title": "Dag 2 — Isometrisk bålstabilitet & Bäckenkontroll",
          "notes": "Fokusera på bålspänning utan att ändra ryggradens position.",
          "exercises": [
            {
              "name": "Höftlyft / Glute Bridge",
              "category": "rehab",
              "purpose": "Aktiverar sätesmuskler och stabiliserar bäckenet.",
              "targetMuscles": "Sätesmuskler, hamstrings, ländrygg",
              "sets": 3,
              "reps": "12 reps",
              "rest": "30 sekunder",
              "duration": 5,
              "difficulty": "Intermediate",
              "description": "Ligg på rygg med böjda knän; pressa genom hälarna för att lyfta höften."
            },
            {
              "name": "Hunden / Bird-Dog Core-övning",
              "category": "rehab",
              "purpose": "Diagonal stabilitet för bål och ländrygg.",
              "targetMuscles": "Multifidus, sneda bukmuskler, ryggsträckare",
              "sets": 3,
              "reps": "5 reps per sida (10s hålltid)",
              "rest": "30 sekunder",
              "duration": 5,
              "difficulty": "Intermediate",
              "description": "Sträck höger arm och vänster ben samtidigt medan kroppen hålls helt stilla."
            }
          ]
        }
      ]
    }
  ]
};

export default function ProgramJsonImportModal({
  isOpen,
  onClose,
  onImportSuccess
}: ProgramJsonImportModalProps) {
  const { t, lang } = useLanguage();
  const [jsonText, setJsonText] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  if (!isOpen) return null;

  const currentSampleJson = JSON.stringify(SAMPLE_PROGRAMS[lang] || SAMPLE_PROGRAMS.en, null, 2);

  const handleImport = async () => {
    setErrorMsg(null);
    setSuccessMsg(null);

    if (!jsonText.trim()) {
      setErrorMsg(t('programJsonEmptyError', 'لطفاً محتوای JSON برنامه تمرینی را وارد کنید.'));
      return;
    }

    let parsedData: any;
    try {
      parsedData = JSON.parse(jsonText.trim());
    } catch (e: any) {
      setErrorMsg(t('jsonParseError', undefined, { err: e.message }));
      return;
    }

    try {
      setLoading(true);
      const res = await apiPost('/api/rehab/programs/import-json', parsedData);

      if (res && res.success) {
        setSuccessMsg(t('importSuccessMsg'));
        if (res.data && Array.isArray(res.data)) {
          onImportSuccess(res.data);
        }
        setTimeout(() => {
          onClose();
        }, 1200);
      } else {
        setErrorMsg(res?.error || t('importFailed'));
      }
    } catch (err: any) {
      setErrorMsg(err?.message || t('importFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      if (content) {
        setJsonText(content);
        setErrorMsg(null);
      }
    };
    reader.readAsText(file);
  };

  const handlePasteSample = () => {
    setJsonText(currentSampleJson);
    setErrorMsg(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-purple-200 dark:border-purple-900/40 w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-gray-800 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950/30 dark:to-indigo-950/30">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-purple-600 text-white rounded-xl shadow-md">
              <FileCode className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {t('importJsonModalTitle', 'درون‌ریزی دسته‌جمعی برنامه تمرینی چندروزه (JSON)')}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('multiDayProgramsDesc', 'واردسازی برنامه‌های تمرینی چندروزه با جدول تمرینات روزانه به دیتابیس D1')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {errorMsg && (
            <div className="p-4 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800/60 rounded-xl flex items-start gap-3 text-red-700 dark:text-red-300 text-sm">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <div>{errorMsg}</div>
            </div>
          )}

          {successMsg && (
            <div className="p-4 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 rounded-xl flex items-center gap-3 text-emerald-700 dark:text-emerald-300 text-sm">
              <CheckCircle2 className="w-5 h-5 shrink-0" />
              <div>{successMsg}</div>
            </div>
          )}

          {/* Quick Tools & Upload */}
          <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <label className="px-3.5 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-lg flex items-center gap-2 cursor-pointer transition-colors shadow-xs">
                <UploadCloud className="w-4 h-4" />
                <span>{t('uploadJsonFileLabel', 'بارگذاری فایل JSON')}</span>
                <input
                  type="file"
                  accept=".json,application/json"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
              <button
                type="button"
                onClick={handlePasteSample}
                className="px-3 py-1.5 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                <span>{t('pasteSampleJsonBtn', 'جای‌گذاری نمونه کامل')}</span>
              </button>
            </div>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(currentSampleJson);
                setSuccessMsg(t('sampleCopied'));
                setTimeout(() => setSuccessMsg(null), 2000);
              }}
              className="text-xs text-purple-600 dark:text-purple-400 hover:underline flex items-center gap-1 cursor-pointer"
            >
              <Copy className="w-3.5 h-3.5" />
              <span>{t('copyTemplate', 'کپی الگو')}</span>
            </button>
          </div>

          {/* Textarea for JSON content */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {t('jsonInputLabel', 'محتوای ساختاریافته JSON برنامه:')}
            </label>
            <textarea
              rows={12}
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              placeholder={t('jsonInputPlaceholder', 'محتوای JSON را اینجا وارد کنید...')}
              className="w-full p-3 font-mono text-xs bg-gray-950 text-emerald-400 rounded-xl border border-gray-800 focus:outline-hidden focus:ring-2 focus:ring-purple-500 dir-ltr text-left leading-relaxed"
            />
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors cursor-pointer"
          >
            {t('cancel', 'انصراف')}
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={loading}
            className="px-5 py-2 text-xs font-bold text-white bg-purple-600 hover:bg-purple-700 rounded-xl shadow-md transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>{t('importingPrograms', 'در حال ذخیره‌سازی...')}</span>
              </>
            ) : (
              <>
                <UploadCloud className="w-4 h-4" />
                <span>{t('programStartImportBtn', 'درون‌ریزی و ذخیره در دیتابیس')}</span>
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
}
